import { CommonModule, DatePipe } from '@angular/common';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  ApplicationConfig,
  provideZoneChangeDetection,
  isDevMode,
} from '@angular/core';
import {
  InMemoryScrollingOptions,
  InMemoryScrollingFeature,
  withInMemoryScrolling,
  provideRouter,
} from '@angular/router';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { errorInterceptor } from '@core/interceptors/error/error.interceptor';
import { jwtInterceptor } from '@core/interceptors/jwt/jwt.interceptor';
import { loaderInterceptor } from '@core/interceptors/loader/loader.interceptor';
import { PartnerListEffect } from '@feature/dashboard/partners/store/partner-list.effect';
import { partnerListReducer } from '@feature/dashboard/partners/store/partner-list.reducers';
import { PARTNER_LIST_STATE } from '@feature/dashboard/partners/store/partner-list.state';
import { RecipientListEffect } from '@feature/dashboard/recipients/store/recipient-list.effect';
import { recipientListReducer } from '@feature/dashboard/recipients/store/recipient-list.reducers';
import { RECIPIENT_LIST_STATE } from '@feature/dashboard/recipients/store/recipient-list.state';
import { SenderListEffect } from '@feature/dashboard/senders/store/sender-list.effect';
import { senderListReducer } from '@feature/dashboard/senders/store/sender-list.reducers';
import { SENDER_LIST_STATE } from '@feature/dashboard/senders/store/sender-list.state';
import { ConversionRateEffect } from '@feature/dashboard/settings/country-settings/store/conversion-rate.effect';
import { conversionRateReducer } from '@feature/dashboard/settings/country-settings/store/conversion-rate.reducers';
import { CONVERSION_RATE_STATE } from '@feature/dashboard/settings/country-settings/store/conversion-rate.state';
import { VelocityLimitListEffect } from '@feature/dashboard/settings/velocity-limit/store/velocity-limit-list.effect';
import { velocityLimitListReducer } from '@feature/dashboard/settings/velocity-limit/store/velocity-limit-list.reducers';
import { VELOCITY_LIMIT_LIST_STATE } from '@feature/dashboard/settings/velocity-limit/store/velocity-limit-list.state';
import { TransactionListEffect } from '@feature/dashboard/transactions/store/transaction-list.effect';
import { transactionListReducer } from '@feature/dashboard/transactions/store/transaction-list.reducers';
import { TRANSACTION_LIST_STATE } from '@feature/dashboard/transactions/store/transaction-list.state';
import { PermissionListEffect } from '@feature/dashboard/user-management/permissions/store/permission-list.effect';
import { permissionListReducer } from '@feature/dashboard/user-management/permissions/store/permission-list.reducers';
import { PERMISSION_LIST_STATE } from '@feature/dashboard/user-management/permissions/store/permission-list.state';
import { RoleListEffect } from '@feature/dashboard/user-management/roles/store/role-list.effect';
import { roleListReducer } from '@feature/dashboard/user-management/roles/store/role-list.reducers';
import { ROLE_LIST_STATE } from '@feature/dashboard/user-management/roles/store/role-list.state';
import { UserListEffect } from '@feature/dashboard/user-management/users/store/user-list.effect';
import { userListReducer } from '@feature/dashboard/user-management/users/store/user-list.reducers';
import { USER_LIST_STATE } from '@feature/dashboard/user-management/users/store/user-list.state';
import { PartnerWalletListEffect } from '@feature/dashboard/wallets/store/partner-wallet-list/partner-wallet-list.effect';
import { partnerWalletListReducer } from '@feature/dashboard/wallets/store/partner-wallet-list/partner-wallet-list.reducers';
import { PARTNER_WALLET_LIST_STATE } from '@feature/dashboard/wallets/store/partner-wallet-list/partner-wallet-list.state';
import { SenderWalletListEffect } from '@feature/dashboard/wallets/store/sender-wallet-list/sender-wallet-list.effect';
import { senderWalletListReducer } from '@feature/dashboard/wallets/store/sender-wallet-list/sender-wallet-list.reducers';
import { SENDER_WALLET_LIST_STATE } from '@feature/dashboard/wallets/store/sender-wallet-list/sender-wallet-list.state';
import { provideEffects } from '@ngrx/effects';
import { provideStore, provideState } from '@ngrx/store';
import { CountryEffect } from '@sharedModule/store/country/country.effect';
import { countryReducer } from '@sharedModule/store/country/country.reducers';
import { COUNTRY_STATE } from '@sharedModule/store/country/country.state';
import { DestinationCountryEffect } from '@sharedModule/store/destination-country/destination-country.effect';
import { destinationCountryReducer } from '@sharedModule/store/destination-country/destination-country.reducers';
import { DESTINATION_COUNTRY_STATE } from '@sharedModule/store/destination-country/destination-country.state';
import { SourceCountryEffect } from '@sharedModule/store/source-country/source-country.effect';
import { sourceCountryReducer } from '@sharedModule/store/source-country/source-country.reducers';
import { SOURCE_COUNTRY_STATE } from '@sharedModule/store/source-country/source-country.state';
import { routes } from './app.routes';

const scrollConfig: InMemoryScrollingOptions = {
  scrollPositionRestoration: 'top',
  anchorScrolling: 'enabled',
};

const inMemoryScrollingFeature: InMemoryScrollingFeature =
  withInMemoryScrolling(scrollConfig);

export const appConfig: ApplicationConfig = {
  providers: [
    CommonModule,
    DatePipe,
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, inMemoryScrollingFeature),
    provideHttpClient(
      withInterceptors([jwtInterceptor, errorInterceptor, loaderInterceptor])
    ),
    provideAnimationsAsync(),
    provideStore(),
    provideState({
      name: COUNTRY_STATE,
      reducer: countryReducer,
    }),
    provideState({
      name: SOURCE_COUNTRY_STATE,
      reducer: sourceCountryReducer,
    }),
    provideState({
      name: DESTINATION_COUNTRY_STATE,
      reducer: destinationCountryReducer,
    }),
    provideState({
      name: PARTNER_LIST_STATE,
      reducer: partnerListReducer,
    }),
    provideState({
      name: PARTNER_WALLET_LIST_STATE,
      reducer: partnerWalletListReducer,
    }),
    provideState({
      name: SENDER_WALLET_LIST_STATE,
      reducer: senderWalletListReducer,
    }),
    provideState({
      name: TRANSACTION_LIST_STATE,
      reducer: transactionListReducer,
    }),
    provideState({
      name: SENDER_LIST_STATE,
      reducer: senderListReducer,
    }),
    provideState({
      name: RECIPIENT_LIST_STATE,
      reducer: recipientListReducer,
    }),
    provideState({
      name: USER_LIST_STATE,
      reducer: userListReducer,
    }),
    provideState({
      name: ROLE_LIST_STATE,
      reducer: roleListReducer,
    }),
    provideState({
      name: PERMISSION_LIST_STATE,
      reducer: permissionListReducer,
    }),
    provideState({
      name: VELOCITY_LIMIT_LIST_STATE,
      reducer: velocityLimitListReducer,
    }),
    provideState({
      name: CONVERSION_RATE_STATE,
      reducer: conversionRateReducer,
    }),
    provideEffects([
      CountryEffect,
      SourceCountryEffect,
      DestinationCountryEffect,
      PartnerListEffect,
      PartnerWalletListEffect,
      SenderWalletListEffect,
      TransactionListEffect,
      SenderListEffect,
      RecipientListEffect,
      UserListEffect,
      RoleListEffect,
      PermissionListEffect,
      VelocityLimitListEffect,
      ConversionRateEffect,
    ]),
    provideStoreDevtools({ maxAge: false, logOnly: !isDevMode() }),
  ],
};
